import React, { FC } from "react";
import { Grid } from "@mui/material";
import { Box, styled, useTheme } from "@mui/system";
import Containers from "../../../components/Containers";
import { H2, H3, Paragraph } from "../../../components/Typography";
import PieChart from "../../../components/apexCharts/PieChart";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import PolarChart from "../../../components/apexCharts/PolarChart";
import ColumnChart from "../../../components/apexCharts/ColumnChart";
import ColumnChart2 from "../../../components/apexCharts/ColumnChart2";
import HeatmapChart from "../../../components/apexCharts/HeatmapChart";
import BasicAreaChart from "../../../components/apexCharts/BasicAreaChart";
import ComparisonChart from "../../../components/apexCharts/ComparisonChart";
import BasicLineChart1 from "../../../components/apexCharts/BasicLineChart1";
import ThreeColumnChart from "../../../components/apexCharts/ThreeColumnChart";
import BasicDonutChart2 from "../../../components/apexCharts/BasicDonutChart2";
import AdvanceAreaChart from "../../../components/apexCharts/AdvanceAreaChart";
import AdvanceLineChart from "../../../components/apexCharts/AdvanceLineChart";

const ChartHeader = styled(Box)(() => ({
  padding: "1rem",
}));

const StyledChartBox = styled(Box)(({ theme }) => ({
  "& h2": {
    marginTop: "-20px",
    fontWeight: 600,
  },
  "& p": {
    fontWeight: 600,
    color: theme.palette.grey.main,
  },
  [theme.breakpoints.down("md")]: {
    width: "calc(100vw - 60px)",
  },
}));

const ApexCharts: FC = () => {
  const theme = useTheme();
  const { ChartContainer } = Containers();
  const chartColor = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
  ];
  const basicColumnData = [10, 20, 30, 40, 50, 60, 70, 60, 50, 40, 30, 20, 10];
  const chart2Data = [55, 43, 35, 60, 50, 40, 35, 37, 48];

  const advanceChartData = [
    {
      name: "A",
      data: [40, 80, 20, 90, 110, 130, 120, 135, 145],
    },
    {
      name: "B",
      data: [40, 100, 70, 110, 90, 120, 140, 155, 160],
    },
  ];

  const advanceChartCategory = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
  ];

  return (
    <>
      <Breadcrumb name="Charts" subName="Apex Charts" path="/dashboard/v1" />

      <Grid container spacing={3}>
        {chartColor.map((color) => (
          <Grid item xs={12} md={3}>
            <ChartContainer
              sx={{
                padding: 0,
                paddingBottom: "15px",
                textAlign: "center",
              }}
            >
              <StyledChartBox>
                <ColumnChart chartData={basicColumnData} color={color} />
                <H2 color={color}>6,708</H2>
                <Paragraph>50% Average</Paragraph>
              </StyledChartBox>
            </ChartContainer>
          </Grid>
        ))}

        <Grid item xs={12} md={6}>
          <ChartContainer sx={{ pb: 0, pl: "5px" }}>
            <H3 sx={{ mx: "20px", mb: "10px" }}>Basic 1 Column Chart 1</H3>
            <ColumnChart2
              chartData={chart2Data}
              color={theme.palette.primary.main}
            />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer sx={{ pb: 0, pl: "5px" }}>
            <H3 sx={{ mx: "20px", mb: "10px" }}>Basic 1 Column Chart 1</H3>
            <ColumnChart2
              chartData={chart2Data}
              color={theme.palette.warning.main}
            />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer sx={{ p: 0, height: 310 }}>
            <ChartHeader>
              <H3>Basic Area Chart 1</H3>
            </ChartHeader>
            <BasicAreaChart
              height={270}
              color={theme.palette.primary.main}
              data={[30, 40, 20, 50, 40, 80, 90]}
            />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer sx={{ p: 0, height: 310 }}>
            <ChartHeader>
              <H3>Basic Area Chart 2</H3>
            </ChartHeader>
            <BasicAreaChart
              height={270}
              color={theme.palette.warning.main}
              data={[30, 10, 40, 10, 40, 20, 90]}
            />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer sx={{ p: 0, pr: "10px" }}>
            <H3 p="1.5rem" pb="0rem">
              Advance Multi Line Chart 1
            </H3>
            <AdvanceLineChart
              chartData={advanceChartData}
              colors={[theme.palette.primary.main, theme.palette.grey[400]]}
              categories={advanceChartCategory}
              height={300}
            />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer sx={{ p: 0, pr: "10px" }}>
            <H3 p="1.5rem" pb="0rem">
              Advance Multi Line Chart 2
            </H3>
            <AdvanceLineChart
              chartData={advanceChartData}
              colors={[theme.palette.success.main, theme.palette.grey[400]]}
              categories={advanceChartCategory}
              height={300}
            />
          </ChartContainer>
        </Grid>
      </Grid>

      <Grid container spacing={3} my="0px">
        <Grid item xs={12} md={8}>
          <ChartContainer sx={{ height: 360 }}>
            <H3>Comparison Char</H3>
            <ComparisonChart />
          </ChartContainer>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartContainer sx={{ height: 360 }}>
            <H3 mb="40px">Pie Chart</H3>
            <PieChart />
          </ChartContainer>
        </Grid>
      </Grid>

      <Grid container spacing={3} my="0px">
        <Grid item xs={12} md={8}>
          <ChartContainer sx={{ pb: 0, pl: "5px", height: "360px" }}>
            <H3 sx={{ mx: "20px", mb: "30px" }}>Three Column Chart</H3>
            <ThreeColumnChart height={260} />
          </ChartContainer>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartContainer sx={{ height: 360 }}>
            <H3 mb="1.5rem">Donut Chart</H3>
            <BasicDonutChart2 height={260} />
          </ChartContainer>
        </Grid>
      </Grid>

      <Grid container spacing={3} my="0px">
        <Grid item xs={12} md={8}>
          <ChartContainer sx={{ height: 350 }}>
            <H3>Basic Line Chart 1</H3>
            <BasicLineChart1
              data={[
                40, 80, 20, 90, 30, 80, 40, 90, 20, 80, 30, 45, 50, 110, 90,
                145, 120, 135, 120, 140,
              ]}
              color={theme.palette.primary.main}
              height={270}
            />
          </ChartContainer>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartContainer sx={{ height: 350 }}>
            <H3>Basic Line Chart 2</H3>
            <BasicLineChart1
              data={[40, 80, 20, 90, 30, 80, 40]}
              color={theme.palette.primary.main}
              height={270}
            />
          </ChartContainer>
        </Grid>
      </Grid>

      <Grid container spacing={3} my="0px">
        <Grid item xs={12} md={8}>
          <ChartContainer sx={{ p: 0, pr: "5px", height: 400 }}>
            <H3 p="1.5rem">Advance Multiline Area Chart</H3>
            <AdvanceAreaChart
              chartData={[
                {
                  name: "A",
                  data: [20, 90, 20, 90, 20, 90, 20],
                },
                {
                  name: "B",
                  data: [90, 20, 90, 20, 90, 20, 90],
                },
              ]}
              colors={[theme.palette.primary.main, theme.palette.primary[300]]}
              categories={[
                "00.00",
                "01.00",
                "02.00",
                "03.00",
                "04.00",
                "05.00",
                "06.00",
              ]}
              height={320}
            />
          </ChartContainer>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartContainer sx={{ p: 0, height: 400 }}>
            <H3 p="1.5rem" pb="1rem">
              Polar Chart
            </H3>
            <PolarChart />
          </ChartContainer>
        </Grid>

        <Grid item xs={12}>
          <ChartContainer sx={{ p: 0 }}>
            <Box p="1.5rem" pb="0rem">
              <H3>Heatmap Chart</H3>
            </Box>
            <HeatmapChart
              chartData={[
                {
                  name: "A",
                  data: [40, 80, 20, 90, 30, 80, 40, 90, 130, 120, 135, 145],
                },
                {
                  name: "B",
                  data: [40, 80, 20, 90, 30, 80, 40, 90, 130, 120, 135, 145],
                },
                {
                  name: "C",
                  data: [40, 80, 20, 90, 30, 80, 40, 90, 130, 120, 135, 145],
                },
                {
                  name: "D",
                  data: [40, 80, 20, 90, 30, 80, 40, 90, 130, 120, 135, 145],
                },
                {
                  name: "E",
                  data: [40, 80, 20, 90, 30, 80, 40, 90, 130, 120, 135, 145],
                },
                {
                  name: "F",
                  data: [40, 80, 20, 90, 30, 80, 40, 90, 130, 120, 135, 145],
                },
              ]}
              colors={["#9575cd", "#663399", "#b39ddb", "#d1c4e9"]}
              categories={[
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
              ]}
              height={400}
            />
          </ChartContainer>
        </Grid>
      </Grid>
    </>
  );
};

export default React.memo(ApexCharts);
